import type { FC, ReactNode } from 'react'

import { useEffect, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MaskMark } from '@/components/generals/mask-mark'
import { NAV_LINKS, siteConfig } from '@/constants/site'

export const SiteHeader: FC = (): ReactNode => {
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        if (!open) return

        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        window.addEventListener('keydown', onKeyDown)

        return () => window.removeEventListener('keydown', onKeyDown)
    }, [open])

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-50 border-b transition-colors duration-500',
                scrolled || open
                    ? 'border-border bg-background/85 backdrop-blur-lg'
                    : 'border-transparent bg-transparent'
            )}
        >
            <div className='mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-5 sm:h-18 sm:px-8'>
                <a
                    href='#stage'
                    className='flex items-center gap-2.5 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                >
                    <MaskMark className='size-8' />
                    <span className='font-heading text-2xl font-bold tracking-tight'>
                        {siteConfig.name}
                    </span>
                </a>

                <nav aria-label='ניווט ראשי' className='hidden md:block'>
                    <ul className='flex items-center gap-1'>
                        {NAV_LINKS.map((link) => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    className='rounded-full px-4 py-2 text-sm text-foreground/75 transition-colors duration-300 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className='flex items-center gap-2'>
                    <a
                        href='#trial'
                        onClick={() => setOpen(false)}
                        className='inline-flex min-h-11 items-center rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground transition-all duration-300 hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:px-5'
                    >
                        שיעור ניסיון
                    </a>

                    <button
                        type='button'
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        onClick={() => setOpen((value) => !value)}
                        className='inline-flex size-11 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:border-primary/50 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:hidden'
                    >
                        {open ? <X aria-hidden='true' className='size-5' /> : <Menu aria-hidden='true' className='size-5' />}
                        <span className='sr-only'>
                            {open ? 'סגירת התפריט' : 'פתיחת התפריט'}
                        </span>
                    </button>
                </div>
            </div>

            {open && (
                <nav id='mobile-menu' aria-label='ניווט ראשי' className='border-t border-border md:hidden'>
                    <ul className='mx-auto flex max-w-7xl flex-col px-5 py-3'>
                        {NAV_LINKS.map((link) => (
                            <li key={link.href}>
                                <a
                                    href={link.href}
                                    onClick={() => setOpen(false)}
                                    className='block rounded-xl px-3 py-3 text-base text-foreground/85 transition-colors hover:bg-foreground/[0.05] hover:text-primary'
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>
            )}
        </header>
    )
}
